<script lang="ts">
  import { untrack } from "svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"

  const { timezone, recipientLabel, recipientEmail, sending, disabled, error, onSend, onClose } =
    $props<{
      timezone: string
      recipientLabel: string
      recipientEmail: string
      sending: boolean
      disabled: boolean
      error: string
      onSend: (date: string) => Promise<void>
      onClose: () => void
    }>()

  function today() {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date())
    const part = (name: string) => parts.find((value) => value.type === name)?.value ?? ""

    // The picker represents a calendar day in the browser timezone, not an instant.
    return new Date(`${part("year")}-${part("month")}-${part("day")}T12:00:00`)
  }
  let referenceDate = $state<Date | null>(untrack(today))
  const referenceDateIso = $derived.by(() => {
    if (!(referenceDate instanceof Date) || !Number.isFinite(referenceDate.getTime())) return ""

    // Preserve the picker’s calendar fields; UTC serialization could move the day.
    return [
      String(referenceDate.getFullYear()).padStart(4, "0"),
      String(referenceDate.getMonth() + 1).padStart(2, "0"),
      String(referenceDate.getDate()).padStart(2, "0"),
    ].join("-")
  })
  const valid = $derived(
    /^\d{4}-\d{2}-\d{2}$/.test(referenceDateIso) &&
      referenceDateIso >= "0001-01-29" &&
      referenceDateIso <= "9999-11-19",
  )
</script>

<ModalDialog title="Morgenübersicht senden" appearance="plain" size="small" {onClose}>
  {#snippet content()}
    <div class="send-form form">
      <p
        ><strong>{recipientLabel}</strong><br />{recipientEmail ||
          "Ihre hinterlegte Konto-E-Mail-Adresse"}</p
      >
      <Input
        type="date"
        placeholder="Datum der Übersicht"
        bind:value={referenceDate}
        brightBackground={true}
        {disabled}
        helperText={`Kalendertag in ${timezone}`}
      />
      <p>
        Die E-Mail wird sofort gesendet. Das gewählte Datum bestimmt die Übersicht; verwendet werden
        die aktuell gespeicherten Daten.
      </p>
      {#if error}<p role="alert">{error}</p>{/if}
    </div>
  {/snippet}
  {#snippet footerSnippet()}
    <div class="actions">
      <CTA type="secondaryButton" content="Abbrechen" darkMode={false} onClick={onClose} />
      <CTA
        type="primaryButton"
        content={sending ? "Wird angefordert …" : "Jetzt senden"}
        darkMode={false}
        disabled={disabled || !valid}
        onClick={() => {
          if (!disabled && valid) void onSend(referenceDateIso)
        }}
      />
    </div>
  {/snippet}
</ModalDialog>

<style>
  .send-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
  }
  p {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.5;
    overflow-wrap: anywhere;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
